"use client"

import React, { ReactElement, useState } from "react"
import { useTranslations } from "next-intl"
import classNames from "classnames"
import Image from "next/image"
import ModalContent from "src/components/LandingPage/ModalContent"
import styles from "../../styles/LandingPage/Patients.module.scss"
import SText from "src/components/Text/SText"
import NavBarPatients from "src/components/LandingPage/navbars/NavBarPatients"
import Button from "src/components/Button/Button"
import { ReactCompareSlider, ReactCompareSliderHandle, ReactCompareSliderImage } from "react-compare-slider"
import { Dialog } from "@mui/material"
import SectionSeven from "src/components/Patient/Sections/SectionSeven"
import SectionEight from "src/components/Patient/Sections/SectionEight"
import SectionNine from "src/components/Patient/Sections/SectionNine"
import SectionTen from "src/components/Patient/Sections/SectionTen"
import StudyParticipationModal from "src/components/modals/StudyParticipationModal"
import ForDoctorsModal from "src/components/modals/ForDoctorsModal"
import PartnerModal from "src/components/modals/PartnerModal"
import Explanation from "src/content/Explanation"
import AboutUs from "src/content/AboutUs"
import Network from "src/content/Network"
import Advisor from "src/content/Advisor"
import Team from "src/content/Team"
import NewsletterModal from "src/components/modals/NewsletterModal"
import PatientHero from "src/heroes/PatientHero"
import SkinuvitaDrawer from "src/components/drawer/skinuvita_drawer"
import SkinuvitaSeoHead from "src/components/LandingPage/SkinuvitaSeoHead"
import { faqPageSchema } from "src/seo/jsonLd"
import BehindNavBar from "src/components/BehindNavBar/BehindNavBar"
import StyledText from "src/typography/typography-component"
import skColors from "styles/skColors"
import Advantages from "src/components/Patient/advantages"
import VideoSection from "src/components/Patient/video"
import DiseasesInformationSection from "src/components/Patient/DiseasesInformationSection"
import TherapySetSection from "src/components/Patient/therapySetSection"
import CriteriaSection from "src/components/Patient/criteria/criteriaSection"
import Awards from "src/content/Awards"
import FAQ from "src/content/faq/faq"
import DoctorSection from "src/content/DoctorSection"
import Insurances from "src/content/insurances/Insurances"
import FamousFrom from "src/content/famous_from/FamousFrom"
import Footer from "src/components/footer"
import ContactArea from "src/components/ContactArea/ContactArea"
import { Routes, useTypedRouter } from "../data/routes"
import { TrackedActions } from "../tracking/trackedActions"

export interface InternalFaqItem {
  type: "internal_faq"
  value: {
    question: string
    answer: string
    answerJSX: ReactElement
  }
}

enum ModalState {
  STUDIENTEILNAHME = "STUDIENTEILNAHME",
  FOR_DOCTORS = "FOR_DOCTORS",
  BECOME_PARTNER = "BECOME_PARTNER",
  CONTACT = "CONTACT",
  NEWSLETTER = "NEWSLETTER",
}

export default function LandingContent() {
  const t = useTranslations("index")
  const router = useTypedRouter()
  const [show, setShow] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState<ModalState | null>(null)

  const handleClose = () => setShow(false)
  const handleShow = () => setShow(true)
  const openNewsletterModal = () => setIsModalOpen(ModalState.NEWSLETTER)
  const closeModal = () => setIsModalOpen(null)


  const toggleLocale = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    const newLocale = router.locale === "de" ? "en" : "de"
    const asPath = router.asPath ?? Routes.home
    router.push(asPath, asPath, { locale: newLocale }).catch((err) => {
      console.error("Locale switch failed:", err)
    })
  }

  const landingContentFaq: InternalFaqItem[] = [
     {
       type: "internal_faq",
       value: {
         question: t("faq.q1.title"),
         answer: t("faq.q1.subtitle"),
         answerJSX: <StyledText variant={"pRegular"}>{t("faq.q1.subtitle")}</StyledText>,
       },
     },
     {
       type: "internal_faq",
       value: {
         question: t("faq.q2.title"),
         answer: t("faq.q2.subtitle"),
         answerJSX: <StyledText variant={"pRegular"}>{t("faq.q2.subtitle")}</StyledText>,
       },
     },
     {
       type: "internal_faq",
       value: {
         question: t("faq.q3.title"),
         answer: [
           t("faq.q3.subtitle_intro"),
           `1. ${t("faq.q3.step1.title")}: ${t("faq.q3.step1.desc")}`,
           `2. ${t("faq.q3.step2.title")}: ${t("faq.q3.step2.desc")}`,
           `3. ${t("faq.q3.step3.title")}: ${t("faq.q3.step3.desc")}`,
           `4. ${t("faq.q3.step4.title")}: ${t("faq.q3.step4.desc")}`,
           t("faq.q3.closing"),
         ].join(" "),
         answerJSX: (
           <StyledText key={"text-use-skinuvita"} variant={"pRegular"}>
             {t("faq.q3.subtitle_intro")}
             <ol>
               <li>{t("faq.q3.step1.title")}</li>
               <div>{t("faq.q3.step1.desc")}</div>

               <li>{t("faq.q3.step2.title")}</li>
               <div>{t("faq.q3.step2.desc")}</div>

               <li>{t("faq.q3.step3.title")}</li>
               <div>{t("faq.q3.step3.desc")}</div>

               <li>{t("faq.q3.step4.title")}</li>
               <div>{t("faq.q3.step4.desc")}</div>
             </ol>
             {t("faq.q3.closing")}
           </StyledText>
         ),
       },
     },
     {
       type: "internal_faq",
       value: {
         question: t("faq.q4.title"),
         // Kept in step with answerJSX below: FAQ schema must mirror what the page shows.
         answer: `${t("faq.q4.subtitle_intro")} support@skinuvita.de`,
         answerJSX: (
           <StyledText variant={"pRegular"} as={"span"}>
             {t("faq.q4.subtitle_intro")} {" "}
             <a
               style={{
                 color: skColors.turquoise[500],
               }}
               href={"mailto:support@skinuvita.de"}
             >
               support@skinuvita.de
             </a>
           </StyledText>
         ),
       },
     },
     {
       type: "internal_faq",
       value: {
         question: t("faq.q5.title"),
         answer: t("faq.q5.subtitle"),
         answerJSX: <StyledText variant={"pRegular"}>{t("faq.q5.subtitle")}</StyledText>,
       },
     },
     {
       type: "internal_faq",
       value: {
         question: t("faq.q6.title"),
         answer: t("faq.q6.subtitle"),
         answerJSX: <StyledText variant={"pRegular"}>{t("faq.q6.subtitle")}</StyledText>,
       },
     },
   ]

  return (
    <div className={styles.root}>
      <SkinuvitaSeoHead
        title={t("meta.title")}
        description={t("meta.description")}
        canonicalPath={Routes.home}
        jsonLd={[faqPageSchema(landingContentFaq.map((item) => item.value))]}
      />
      <NavBarPatients />
      <div className={styles.constrainWidth}>
        <BehindNavBar />
        <PatientHero />
        <Advantages />
        <VideoSection />
        <Insurances />
        <div className={styles.sectionThreePatient} style={{ backgroundColor: skColors.orange[50] }}>
          <div className={classNames(styles.row, styles.spaceAround, styles.sectionThreeTitle)}>
            <StyledText variant={"h2"} color={skColors.turquoise[600]} className={styles.adaptiveTitleSize}>
              {t("section.therapySuccesses")}
            </StyledText>
          </div>

          <div className={classNames(styles.row, styles.spaceAround, styles.flwr)}>
            {[
              { before: "/images/before_after/hand_before.webp", after: "/images/before_after/hand_after.webp" },
              { before: "/images/before_after/arms_before.webp", after: "/images/before_after/arms_after.webp" },
              { before: "/images/before_after/legs_before.webp", after: "/images/before_after/legs_after.webp" },
            ].map((item, idx) => (
              <div
                key={idx}
                style={{ marginBottom: "24px" }}
                className={classNames(styles.col, styles.spaceAround, styles.itemsCenter)}
              >
                <div style={{ width: "243px", height: "324px", margin: "12px" }}>
                  <ReactCompareSlider
                    handle={<ReactCompareSliderHandle style={{ color: "#92FCF2" }} />}
                    itemOne={<ReactCompareSliderImage src={item.before} />}
                    itemTwo={<ReactCompareSliderImage src={item.after} />}
                  />
                </div>
                <SText variant={"body"} className={styles.beforeAfterLabel} color={"turq500"}>
                  {t("misc.beforeAfterLabel")}
                </SText>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.processContainer}>
          <div className={styles.processTitle}>
            <StyledText variant={"h2"} color={skColors.turquoise[600]}>
              {t("process.titleMain")}
            </StyledText>
            <StyledText variant={"h3"} color={skColors.darkgreen[600]}>
              {t("process.titleSub")}
            </StyledText>
          </div>

          <div className={styles.processSteps}>
            {[
              { img: "/svg/order_process/1_check.svg", textKey: "process.step1" },
              { img: "/svg/order_process/2_doctor.svg", textKey: "process.step2" },
              { img: "/svg/order_process/3_delivering.svg", textKey: "process.step3" },
              { img: "/svg/order_process/4_girl_with_hat.svg", textKey: "process.step4" },
            ].map((step, idx) => (
              <div key={idx} className={styles.orderProcessStep}>
                <Image src={step.img} width={160} height={160} alt={`step-${idx + 1}`} />
                <StyledText variant={"pRegular"} className={styles.designError}>
                  {t(step.textKey)}
                </StyledText>
              </div>
            ))}
          </div>

          <Button hug onClick={() => void TrackedActions.goToSelfCheck()} variant={"primary"}>
            {t("process.cta")}
          </Button>
        </div>
        <DiseasesInformationSection onClick={() => void TrackedActions.goToSelfCheck()} />
        <TherapySetSection />
        <CriteriaSection />
        <Explanation />
        <AboutUs />
        {/*<SectionSeven waveTop={false} waveBottom={false}>*/}
        {/*  <Team />*/}
        {/*</SectionSeven>*/}
        {/*<SectionEight>*/}
        {/*  <Advisor />*/}
        {/*</SectionEight>*/}
        {/* <Network setIsModalOpen={setIsModalOpen} /> */}
        <SectionTen>
          <Awards />
        </SectionTen>
        <FamousFrom />
        <FAQ faqs={landingContentFaq} />
        <SectionNine waveTop={false} waveBottom={false} className={styles.doctorSection}>
          <DoctorSection onClick={() => router.push(Routes.doctors)} />
        </SectionNine>
        <div id="contact-form">
          <ContactArea />
        </div>
        <Footer />
      </div>

      <Dialog open={show} onClose={handleClose} className={styles.modal}>
        <ModalContent onClose={handleClose} />
      </Dialog>

      {isModalOpen === ModalState.STUDIENTEILNAHME && (
        <StudyParticipationModal closeModal={closeModal} isOpen={isModalOpen === ModalState.STUDIENTEILNAHME} />
      )}
      {isModalOpen === ModalState.NEWSLETTER && (
        <NewsletterModal closeModal={closeModal} isOpen={isModalOpen === ModalState.NEWSLETTER} />
      )}
      {isModalOpen === ModalState.FOR_DOCTORS && (
        <ForDoctorsModal closeModal={closeModal} isOpen={isModalOpen === ModalState.FOR_DOCTORS} />
      )}
      {isModalOpen === ModalState.BECOME_PARTNER && (
        <PartnerModal closeModal={closeModal} isOpen={isModalOpen === ModalState.BECOME_PARTNER} />
      )}

      <SkinuvitaDrawer drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} />
    </div>
  )
}
